import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchMyNotificationsPage,
  markNotificationReadNew,
  markAllNotificationsReadNew,
  NOTIFICATIONS_PAGE_SIZE,
} from '../lib/firestore/notificationsNew';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Bell, 
  Briefcase, 
  Calendar, 
  MessageCircle,
  MessageSquare,
  Check,
} from 'lucide-react';
import SafeText from './SafeText';
import EmptyState from './ui/EmptyState';
import LoadErrorNotice from './ui/LoadErrorNotice';
import Skeleton from './ui/skeleton';
import { useToaster } from './ui/Toaster';
import { useUnreadCounts } from '../context/UnreadCountsContext';
import { friendly } from '../lib/errors';
import { listProfileViews, type ProfileViewRecord } from '../lib/firestore/profileViews';
import { listenEmtOffers } from '../lib/firestore/applications';
import type { Offer } from '../types/applications';
import { Eye, DollarSign, FileText, GraduationCap } from 'lucide-react';
import { INSTITUTE_OUTREACH_NOTIFICATION_TYPE, INSTITUTE_UNSUBSCRIBE_PATH } from '../lib/renewal/instituteContactConsent';
import PushDeviceSwitch from './notifications/PushDeviceSwitch';
import {
  getNotificationBody,
  getNotificationHeadline,
  isNotificationRead,
  safeInternalPath,
} from '../lib/notifications/presentation';

export interface EMTNotificationsChatProps {
  /**
   * When "employer", tab links and empty-state copy match the employer shell (/employer/notifications).
   * EMT-only tabs (Offers, Profile Views) are hidden.
   */
  navBasePath?: 'emt' | 'employer';
}

export default function EMTNotificationsChat({ navBasePath = 'emt' }: EMTNotificationsChatProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'all';
  const isEmployerShell = navBasePath === 'employer';
  const TAB_KEYS = ['all', 'messages', 'applications', 'offers', 'profile-views'] as const;
  type TabKey = (typeof TAB_KEYS)[number];
  const normalizedRaw = TAB_KEYS.includes(rawTab as TabKey) ? (rawTab as TabKey) : 'all';
  const tab: TabKey =
    isEmployerShell && (normalizedRaw === 'offers' || normalizedRaw === 'profile-views')
      ? 'all'
      : normalizedRaw;
  const paths =
    isEmployerShell
      ? {
          notifications: '/employer/notifications',
          messages: '/employer/messages',
          applications: '/employer/workforce?tab=applications',
        }
      : {
          notifications: '/notifications',
          messages: '/messages',
          applications: '/applications',
        };
  const currentLocationKey = `${location.pathname}${location.search || ''}`;

  const goNavigate = React.useCallback(
    (target: string, opts?: { preventScrollReset?: boolean }) => {
      const t = target.trim();
      if (t === currentLocationKey) return;
      navigate(t, opts ?? { preventScrollReset: true });
    },
    [currentLocationKey, navigate]
  );

  const selectNotificationTab = React.useCallback(
    (next: TabKey) => {
      const effective =
        isEmployerShell && (next === 'offers' || next === 'profile-views') ? ('all' as TabKey) : next;
      if (effective === tab) return;

      if (location.pathname !== paths.notifications) {
        goNavigate(
          effective === 'all' ? paths.notifications : `${paths.notifications}?tab=${effective}`,
          { preventScrollReset: true }
        );
        return;
      }
      if (effective === 'all') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab: effective }, { replace: true });
      }
    },
    [goNavigate, isEmployerShell, location.pathname, paths.notifications, setSearchParams, tab]
  );
  const { user, profile } = useAuth();
  const { push: toast } = useToaster();
  const { refreshBadgeCounts } = useUnreadCounts();
  
  // Notifications state - use the actual notification type from Firestore
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  /** The first notifications page failed to load (shown instead of "No notifications yet"). */
  const [notificationsLoadFailed, setNotificationsLoadFailed] = useState(false);
  const [notificationsReloadKey, setNotificationsReloadKey] = useState(0);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const [notificationsHasMore, setNotificationsHasMore] = useState(false);
  const [notificationsCursor, setNotificationsCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  
  // Profile Views tab state
  const [profileViewsList, setProfileViewsList] = useState<ProfileViewRecord[]>([]);
  const [profileViewsLoading, setProfileViewsLoading] = useState(false);
  const [profileViewsLoadFailed, setProfileViewsLoadFailed] = useState(false);
  const [profileViewsReloadKey, setProfileViewsReloadKey] = useState(0);
  
  // Offers tab state (real-time from Firestore)
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  /** The offers listener failed — shown instead of "No offers yet". */
  const [offersLoadError, setOffersLoadError] = useState(false);
  
  // Load notifications (paged; newest first via `createdAt` desc + `startAfter` for “Load more”)
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    setNotifications([]);
    setNotificationsCursor(null);
    setNotificationsHasMore(false);
    setNotificationsLoading(true);
    setNotificationsLoadFailed(false);

    (async () => {
      try {
        const page = await fetchMyNotificationsPage(user.uid, NOTIFICATIONS_PAGE_SIZE, null, {
          source: 'server',
        });
        if (cancelled) return;
        setNotifications(page.items as any[]);
        setNotificationsHasMore(page.hasMore);
        setNotificationsCursor(page.endCursor);
        if (import.meta.env.DEV) {
          console.log(
            '[NOTIFS_NEW] first page:',
            page.items.length,
            'hasMore:',
            page.hasMore,
            'unread:',
            page.items.filter((n) => !isNotificationRead(n)).length
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[Notifications] Failed to load notifications:', err);
          setNotifications([]);
          setNotificationsHasMore(false);
          setNotificationsCursor(null);
          setNotificationsLoadFailed(true);
        }
      } finally {
        if (!cancelled) setNotificationsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, notificationsReloadKey]);

  const loadMoreNotifications = async () => {
    if (!user || !notificationsHasMore || notificationsLoadingMore || !notificationsCursor) return;
    setNotificationsLoadingMore(true);
    try {
      const page = await fetchMyNotificationsPage(user.uid, NOTIFICATIONS_PAGE_SIZE, notificationsCursor);
      setNotifications((prev) => [...prev, ...(page.items as any[])]);
      setNotificationsHasMore(page.hasMore);
      setNotificationsCursor(page.endCursor);
    } catch (err) {
      console.error('[Notifications] Load more failed:', err);
      toast({ title: 'Could not load more', description: friendly(err) });
    } finally {
      setNotificationsLoadingMore(false);
    }
  };

  // Load profile views when Profile Views tab is active
  useEffect(() => {
    if (!user || tab !== 'profile-views') return;
    setProfileViewsLoading(true);
    setProfileViewsLoadFailed(false);
    (async () => {
      try {
        const list = await listProfileViews(user.uid, 30, { throwOnError: true });
        setProfileViewsList(list);
      } catch (err) {
        console.warn('[Notifications] Profile views load failed:', err);
        setProfileViewsList([]);
        setProfileViewsLoadFailed(true);
      } finally {
        setProfileViewsLoading(false);
      }
    })();
  }, [user, tab, profileViewsReloadKey]);

  // Load offers when Offers tab is active (real-time subscription)
  useEffect(() => {
    if (!user || tab !== 'offers') return;
    setOffersLoading(true);
    setOffersLoadError(false);
    const unsubscribe = listenEmtOffers(
      user.uid,
      (list) => {
        setOffers(list);
        setOffersLoading(false);
      },
      (err) => {
        console.error('[EMTNotificationsChat] Offers listener failed:', err);
        setOffersLoadError(true);
        setOffersLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user, tab]);

  const visibleNotifications = useMemo(() => {
    if (tab === 'messages') {
      return notifications.filter((n: any) => n?.type === 'message');
    }
    if (tab === 'applications') {
      const appTypes = new Set([
        'application',
        'new_application',
        'application_status',
        'offer',
        'interview',
      ]);
      return notifications.filter((n: any) => appTypes.has(n?.type));
    }
    return notifications;
  }, [notifications, tab]);

  const handleNotificationClick = async (notificationId: string) => {
    if (!user) return;
    
    const notification = notifications.find((n: any) => n.id === notificationId);
    if (!notification) return;
    
    // Mark as read if unread — notifications/{uid}/items/{id} only (no legacy profiles path).
    if (!isNotificationRead(notification)) {
      const path = `notifications/${user.uid}/items/${notificationId}`;
      try {
        await markNotificationReadNew(user.uid, notificationId);
        setNotifications((prev) =>
          prev.map((n: any) =>
            n.id === notificationId ? { ...n, read: true, readAt: n.readAt ?? new Date() } : n
          )
        );
        await refreshBadgeCounts();
      } catch (err) {
        console.warn(
          '[NOTIFS_NEW] mark read failed (check Firestore rules for this path):',
          path,
          err
        );
        toast({
          title: 'Could not mark as read',
          description: friendly(err),
        });
      }
    }
    
    const navOpts = { preventScrollReset: true } as const;
    const href = safeInternalPath((notification as any).href);
    if (href) {
      goNavigate(href, navOpts);
      return;
    }

    const role = profile?.role;
    // Navigate based on notification type
    if ((notification as any).jobId) {
      const jobId = (notification as any).jobId;
      goNavigate(role === 'employer' ? `/employer/jobs/${jobId}` : `/jobs/${jobId}`, navOpts);
      return;
    }
    if (notification.type === 'message') {
      const threadId = (notification as any).threadId;
      if (threadId) {
        goNavigate(
          role === 'employer'
            ? `/employer/messages?threadId=${threadId}`
            : `/messages?threadId=${threadId}`,
          navOpts
        );
      } else {
        goNavigate(role === 'employer' ? '/employer/messages' : '/messages', navOpts);
      }
    } else if (notification.type === 'application') {
      const appId = (notification as any).meta?.applicationId;
      if (appId) {
        goNavigate(role === 'employer' ? `/employer/applications?appId=${appId}` : `/applications?appId=${appId}`, navOpts);
      } else {
        goNavigate(role === 'employer' ? '/employer/applications' : '/applications', navOpts);
      }
    } else if (notification.type === 'offer') {
      const offerId = (notification as any).meta?.offerId;
      const appId = (notification as any).meta?.applicationId;
      if (role === 'employer') {
        if (appId) goNavigate(`/employer/applications?appId=${appId}`, navOpts);
        else goNavigate('/employer/applications', navOpts);
      } else if (offerId || appId) {
        goNavigate(`/applications?offerId=${offerId || appId}`, navOpts);
      } else {
        goNavigate('/applications', navOpts);
      }
    } else if (notification.type === 'document_expiry') {
      const docHref =
        safeInternalPath((notification as any).href) ||
        safeInternalPath((notification as any).link) ||
        '/account#certifications';
      goNavigate(docHref, navOpts);
    } else if (notification.type === 'profile_reminder') {
      const reminderHref = safeInternalPath((notification as any).href) || '/account';
      goNavigate(reminderHref, navOpts);
    } else {
      goNavigate(role === 'employer' ? '/employer/dashboard' : role === 'emt' ? '/dashboard' : '/', navOpts);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user || markingAll) return;
    
    setMarkingAll(true);
    try {
      await markAllNotificationsReadNew(user.uid);
      setNotifications((prev) => prev.map((n: any) => ({ ...n, read: true, readAt: n.readAt ?? new Date() })));
      await refreshBadgeCounts();
      try {
        const page = await fetchMyNotificationsPage(user.uid, NOTIFICATIONS_PAGE_SIZE, null, {
          source: 'server',
        });
        setNotifications(page.items as any[]);
        setNotificationsHasMore(page.hasMore);
        setNotificationsCursor(page.endCursor);
      } catch {
        /* list already optimistic; server resync optional */
      }
      toast({ title: 'All notifications marked as read' });
    } catch (err) {
      console.warn(
        '[NOTIFS_NEW] mark all read failed (check Firestore rules for notifications/{uid}/items/*):',
        `notifications/${user.uid}/items/*`,
        err
      );
      toast({ title: 'Could not mark all as read', description: friendly(err) });
    } finally {
      setMarkingAll(false);
    }
  };

  const formatTime = (timestamp: any): string => {
    if (!timestamp) return 'Recently';
    try {
      const date = timestamp?.toDate?.();
      if (!date || isNaN(date.getTime())) return 'Recently';
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Recently';
    }
  };

  const unreadCount = visibleNotifications.filter((n: any) => !isNotificationRead(n)).length;
  
  // Get user display name from profile
  const userDisplayName = profile?.firstName && profile?.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile?.displayName || user?.email?.split('@')[0] || 'User';
  
  const userInitials = userDisplayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-8 min-w-0 rr-page-enter">
        <div className="min-w-0">
          {/* Single column: chat preview column removed (thread preview disabled; empty “No messages” panel was misleading on this page) */}
          <div className="min-w-0">
            <Card className="h-full bg-[var(--surface-card)] rounded-2xl shadow-sm border border-[var(--border-subtle)] p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Bell className="w-6 h-6 text-rose-500" />
                  <h2 className="text-2xl text-[var(--text-primary)]">Notifications</h2>
                  {unreadCount > 0 && (
                    <span className="px-2 py-1 rr-badge-unread rounded-full text-xs font-semibold">
                      {unreadCount} unread
                    </span>
                  )}
                </div>
                {(tab === 'all' ? notifications.length > 0 : visibleNotifications.length > 0) && unreadCount > 0 && (
                  <Button
                    onClick={handleMarkAllRead}
                    disabled={markingAll}
                    variant="outline"
                    size="sm"
                    className="text-[var(--text-brand)] border-[var(--border-default)] hover:bg-[var(--surface-hover)] min-h-[2.5rem] shrink-0 rr-btn-press"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {markingAll ? 'Marking...' : 'Mark all as read'}
                  </Button>
                )}
              </div>

              {/* Turn notifications on or off for this device (2026-10-15, D1): the dashboard banner's ask also lives here. */}
              <div className="mb-6 rounded-xl border border-[var(--border-default)] px-3" data-testid="notifications-push-setting">
                <PushDeviceSwitch />
              </div>

              {/* Category tabs: only update ?tab= on this page (no navigation to /messages). */}
              <div className="flex flex-wrap gap-2 mb-6 border-b border-[var(--border-default)]" role="tablist">
                <button
                  type="button"
                  onClick={() => selectNotificationTab('all')} aria-selected={tab === 'all'}
                  className={`px-4 py-2 min-h-[2.5rem] flex items-center text-sm font-medium border-b-2 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 rounded-t bg-transparent ${
                    tab === 'all'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline'
                  }`}
                  role="tab"
                  tabIndex={0}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => selectNotificationTab('messages')} aria-selected={tab === 'messages'}
                  className={`px-4 py-2 min-h-[2.5rem] flex items-center text-sm font-medium border-b-2 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 rounded-t bg-transparent ${
                    tab === 'messages'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline'
                  }`}
                  role="tab"
                  tabIndex={0}
                >
                  Messages
                </button>
                <button
                  type="button"
                  onClick={() => selectNotificationTab('applications')} aria-selected={tab === 'applications'}
                  className={`px-4 py-2 min-h-[2.5rem] flex items-center text-sm font-medium border-b-2 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 rounded-t bg-transparent ${
                    tab === 'applications'
                      ? 'border-rose-600 text-rose-600'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline'
                  }`}
                  role="tab"
                  tabIndex={0}
                >
                  Applications
                </button>
                {!isEmployerShell && (
                  <>
                    <button
                      type="button"
                      onClick={() => selectNotificationTab('offers')} aria-selected={tab === 'offers'}
                      className={`px-4 py-2 min-h-[2.5rem] flex items-center text-sm font-medium border-b-2 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 rounded-t bg-transparent ${
                        tab === 'offers'
                          ? 'border-rose-600 text-rose-600'
                          : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline'
                      }`}
                      role="tab"
                      tabIndex={0}
                    >
                      Offers
                    </button>
                    <button
                      type="button"
                      onClick={() => selectNotificationTab('profile-views')} aria-selected={tab === 'profile-views'}
                      className={`px-4 py-2 min-h-[2.5rem] flex items-center text-sm font-medium border-b-2 cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-1 rounded-t bg-transparent ${
                        tab === 'profile-views'
                          ? 'border-rose-600 text-rose-600'
                          : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline'
                      }`}
                      role="tab"
                      tabIndex={0}
                    >
                      Profile Views
                    </button>
                  </>
                )}
              </div>

              <div className="space-y-4 rr-stagger-list">
                {/* Profile Views tab content */}
                {tab === 'profile-views' && (
                  <>
                    {profileViewsLoading && (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} className="h-16 w-full" />
                        ))}
                      </div>
                    )}
                    {!profileViewsLoading && profileViewsLoadFailed && (
                      <LoadErrorNotice
                        message="We couldn't load your profile views."
                        onRetry={() => setProfileViewsReloadKey((k) => k + 1)}
                        testId="profile-views-load-error"
                      />
                    )}
                    {!profileViewsLoading && !profileViewsLoadFailed && (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <Eye className="w-5 h-5 text-rose-600" />
                          <span className="font-semibold text-[var(--text-primary)]">
                            Total profile views: {profileViewsList.length}
                          </span>
                        </div>
                        {profileViewsList.length === 0 ? (
                          <EmptyState 
                            title="No profile views yet" 
                            description="When employers view your public profile, they'll appear here." 
                          />
                        ) : (
                          <div className="space-y-2">
                            {profileViewsList.map((v) => (
                              <div
                                key={v.id}
                                className="p-4 rounded-xl border border-[var(--border-default)] bg-[var(--surface-muted)] flex items-center justify-between"
                              >
                                <span className="text-sm text-[var(--text-secondary)]">
                                  {v.employerOrgId ? `Viewed by organization` : 'Profile viewed'}
                                </span>
                                <span className="text-sm text-[var(--text-muted)]">
                                  {v.createdAt?.toDate ? v.createdAt.toDate().toLocaleString() : 'Recently'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Offers tab content - real data from Firestore */}
                {tab === 'offers' && (
                  <>
                    {offersLoading && (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <Skeleton key={i} className="h-24 w-full" />
                        ))}
                      </div>
                    )}
                    {!offersLoading && (
                      <>
                        <div className="flex items-center gap-3 mb-4">
                          <Briefcase className="w-5 h-5 text-rose-600" />
                          <span className="font-semibold text-[var(--text-primary)]">Job offers ({offers.length})</span>
                        </div>
                        {offersLoadError ? (
                          <p className="text-sm text-[var(--text-brand)]" role="alert" data-testid="offers-load-failed">
                            We couldn't load your offers, so this is not an empty list. Reload the page to try again.
                          </p>
                        ) : offers.length === 0 ? (
                          <EmptyState 
                            title="No offers yet" 
                            description="When employers send you job offers, they'll appear here." 
                          />
                        ) : (
                          <div className="space-y-3">
                            {offers.map((offer) => {
                              const threadId = [offer.employerId, offer.emtId].sort().join('__');
                              const offeredAt = offer.createdAt?.toDate ? offer.createdAt.toDate().toLocaleString() : 'Recently';
                              return (
                                <div
                                  key={offer.id}
                                  className="p-4 rounded-xl border border-[var(--border-default)] bg-[var(--surface-card)] hover:border-rose-200 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-[var(--text-primary)]">
                                        <SafeText value={offer.employerName} fallback="Employer" />
                                      </p>
                                      {(offer as any).jobTitle && (
                                        <p className="text-sm text-[var(--text-secondary)] mt-0.5">
                                          <SafeText value={(offer as any).jobTitle} label="offer.jobTitle" />
                                        </p>
                                      )}
                                      <p className="text-sm text-[var(--text-secondary)] mt-2">
                                        <SafeText value={offer.message} label="offer.message" />
                                      </p>
                                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-[var(--text-muted)]">
                                        {offer.pay && (
                                          <span className="flex items-center gap-1">
                                            <DollarSign className="w-3 h-3" />
                                            <SafeText value={offer.pay} />
                                          </span>
                                        )}
                                        {offer.schedule && (
                                          <span className="flex items-center gap-1">
                                            <FileText className="w-3 h-3" />
                                            <SafeText value={offer.schedule} />
                                          </span>
                                        )}
                                        <span>{offeredAt}</span>
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-2 flex-shrink-0">
                                      <Button
                                        size="sm"
                                        className="bg-rose-600 hover:bg-rose-700 text-white"
                                        onClick={() =>
                                          goNavigate(`${paths.messages}?threadId=${threadId}`, {
                                            preventScrollReset: true,
                                          })
                                        }
                                      >
                                        <MessageSquare className="w-4 h-4 mr-1" />
                                        View conversation
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => goNavigate('/jobs', { preventScrollReset: true })}
                                      >
                                        View jobs
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* All / default tab: notifications list */}
                {tab !== 'profile-views' && tab !== 'offers' && notificationsLoading && (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                )}
                
                {tab !== 'profile-views' && tab !== 'offers' && !notificationsLoading && notificationsLoadFailed && (
                  <LoadErrorNotice
                    message="We couldn't load your notifications."
                    onRetry={() => setNotificationsReloadKey((k) => k + 1)}
                    testId="notifications-load-error"
                  />
                )}
                {tab !== 'profile-views' &&
                  tab !== 'offers' &&
                  !notificationsLoading &&
                  !notificationsLoadFailed &&
                  visibleNotifications.length === 0 &&
                  !notificationsHasMore && (
                    <EmptyState
                      title={
                        tab === 'messages'
                          ? 'No message notifications'
                          : tab === 'applications'
                            ? 'No application notifications'
                            : 'No notifications yet'
                      }
                      description={
                        tab === 'messages'
                          ? 'When someone sends you a message, it will appear here. You can still open Messages from the nav to read your inbox.'
                          : tab === 'applications'
                            ? 'Application and offer updates will show here when they arrive.'
                            : isEmployerShell
                              ? "You'll see notifications here for new applications, messages, and other updates related to your jobs."
                              : "You'll see notifications here when employers contact you or when there are updates to your applications."
                      }
                    />
                  )}

                {tab !== 'profile-views' &&
                  tab !== 'offers' &&
                  !notificationsLoading &&
                  visibleNotifications.length === 0 &&
                  notificationsHasMore && (
                    <p className="text-center text-sm text-[var(--text-muted)] py-4">
                      Nothing in this category in the loaded notifications yet. Use &quot;Load more&quot; to fetch older items.
                    </p>
                  )}

                {tab !== 'profile-views' && tab !== 'offers' && !notificationsLoading && visibleNotifications.map((notification: any) => {
                  // Determine icon based on notification type
                  let IconComponent = Bell;
                  const notifType = notification.type;
                  if (notifType === 'new_application' || notifType === 'application_status' || notifType === 'application') {
                    IconComponent = Briefcase;
                  } else if (notification.status === 'interview' || notifType === 'interview') {
                    IconComponent = Calendar;
                  } else if (notifType === 'message') {
                    IconComponent = MessageCircle;
                  } else if (notifType === 'offer') {
                    IconComponent = Briefcase;
                  } else if (notifType === 'document_expiry') {
                    IconComponent = FileText;
                  } else if (notifType === INSTITUTE_OUTREACH_NOTIFICATION_TYPE) {
                    IconComponent = GraduationCap;
                  }

                  const read = isNotificationRead(notification);
                  const headline = getNotificationHeadline(notification as Record<string, unknown>);
                  const detail = getNotificationBody(notification as Record<string, unknown>);
                  const daysUntil =
                    typeof notification.daysUntil === 'number' ? notification.daysUntil : null;
                  const isDocExpiry = notifType === 'document_expiry';
                  const isExpiredDoc = isDocExpiry && daysUntil !== null && daysUntil < 0;
                  const isCriticalDoc =
                    isDocExpiry && daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
                  const unreadDocCardClass = isExpiredDoc || isCriticalDoc
                    ? 'rr-notif-unread-critical'
                    : 'rr-notif-unread-warning';
                  const unreadIconClass = isExpiredDoc || isCriticalDoc
                    ? 'bg-gradient-to-r from-red-600 to-red-500'
                    : 'bg-gradient-to-r from-amber-500 to-amber-400';

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification.id)}
                      className={`p-6 rounded-xl border transition-all hover:shadow-md cursor-pointer rr-card-hover ${
                        !read
                          ? isDocExpiry
                            ? unreadDocCardClass
                            : 'rr-notif-unread'
                          : 'bg-[var(--surface-muted)] border-[var(--border-default)]'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${
                          !read
                            ? isDocExpiry
                              ? unreadIconClass
                              : 'bg-gradient-to-r from-rose-600 to-rose-500'
                            : 'bg-[var(--text-muted)]'
                        }`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <h3 className="font-semibold text-[var(--text-primary)]">
                              <SafeText value={headline} label="notification.headline" />
                            </h3>
                            {notification.createdAt && (
                              <span className="shrink-0 text-sm text-[var(--text-muted)]">
                                {formatTime(notification.createdAt)}
                              </span>
                            )}
                          </div>
                          <p className="text-[var(--text-secondary)]">
                            <SafeText value={detail} label="notification.detail" />
                          </p>
                          {notifType === INSTITUTE_OUTREACH_NOTIFICATION_TYPE ? (
                            <p className="mt-2 text-xs text-[var(--text-muted)]" data-testid="institute-message-unsubscribe">
                              Sent by a training institute because you allowed renewal messages.{' '}
                              <button
                                type="button"
                                className="font-medium text-[var(--text-brand)] underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(INSTITUTE_UNSUBSCRIBE_PATH);
                                }}
                              >
                                Stop messages from training institutes
                              </button>
                            </p>
                          ) : null}
                          {(notification as any).orgId && (
                            <p className="text-xs text-[var(--text-muted)] mt-1">
                              <SafeText value={(notification as any).orgId} label="notification.orgId" />
                            </p>
                          )}
                        </div>

                        {!read && (
                          <div className="mt-1 h-3 w-3 shrink-0 rounded-full bg-rose-500"></div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {tab !== 'profile-views' && tab !== 'offers' && !notificationsLoading && notificationsHasMore && notifications.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void loadMoreNotifications()}
                      disabled={notificationsLoadingMore}
                      className="min-h-[2.5rem] border-[var(--border-default)] text-[var(--text-brand)] hover:bg-[var(--surface-hover)] rr-btn-press"
                    >
                      {notificationsLoadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
    </div>
  );
}
